import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import { ReactComponent as LogoIcon } from "../../assets/icons/emotiiBigLogo.svg";
import { ReactComponent as DashboardIcon } from "../../assets/icons/sidebar/DashboardIcon.svg";
import { ReactComponent as Members } from "../../assets/icons/sidebar/members.svg";
import { ReactComponent as ApiKeys } from "../../assets/icons/sidebar/apiKeys.svg";
import { ReactComponent as Subscription } from "../../assets/icons/sidebar/subscription.svg";
import { ReactComponent as Project } from "../../assets/icons/sidebar/project.svg";
import { ReactComponent as Support } from "../../assets/icons/sidebar/support.svg";
import { BookOpenText, Presentation, Video, Users } from "lucide-react";

import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useExpanded } from "../../hooks/useExpanded";

import {
  DASHBOARD,
  API_KEYS,
  GLOSSARY,
  WEBSITE_TRANSLATION,
  PROJECT,
  SUBSCRIPTIONS,
  USERS,
  SUPPORT,
  VIDEO_TRANSLATION,
  PRESENTATIONS,
  REALTIME_API,
  VIDEO_API,
  MEETINGS_API,
  CONFERENCES,
  EMOTII_FOR_TEAMS,
} from "../../constants/OptionsConstants";
import ProjectsMenu from "./ProjectsMenu";
import UserProfileCard from "./UserProfileCard";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useProjectContext } from "../../context/useProjectContext";
import { useKeysContext } from "../../context/useKeysContext";
import { productSlugForPath } from "../../constants/productConstants";
// Embed icon component (matches the icon used in the embed keys table)
const EmbedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.66667 14.1667L2.5 10L6.66667 5.83334M13.3333 5.83334L17.5 10L13.3333 14.1667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const MenuItem = ({ icon, onClick, text, style, selected, disabled, count }) => {
  return (
    <ItemContainer onClick={onClick} selected={selected} style={style} disabled={disabled}>
      {icon && <IconWrapper selected={selected}>{icon}</IconWrapper>}

      {text && (
        <ItemTextContainer selected={selected}>
          <p>{text}</p>
          {count !== undefined && <CountBadge theme={selected ? 'selected' : 'default'}>{count}</CountBadge>}
        </ItemTextContainer>
      )}
    </ItemContainer>
  );
};

const SideBarMenu = ({ setCreateNewProjectModalOpen, setShowSupportModal }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { subscription } = useLicensingContext();
  const { meetingsUsersCount, teamsUsersCount, isAdmin, canAccessProduct } = useProjectContext();
  const { keysCount, realtimeKeysCount, videoApiKeysCount, meetingsApiKeysCount, embedKeysCount } = useKeysContext();

  const navigate = useNavigate();

  const ref = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target) && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    goTo(path);
  };

  const goTo = (path) => {
    // Block navigation for admin-only paths when user is not admin
    if (path === "/subscriptions" && !isAdmin) {
      return;
    }

    const requiredSlug = productSlugForPath(path);
    if (requiredSlug && !canAccessProduct(requiredSlug)) {
      return;
    }

    navigate({
      pathname: path,
      search: createSearchParams(searchParams).toString(),
    });
  };


  const getMenuItemCount = (itemType) => {
    switch (itemType) {
      case 'meetings_seats':
        return meetingsUsersCount || undefined;
      case 'teams_seats':
        return teamsUsersCount || undefined;
      case 'api':
        return keysCount || undefined;
      case 'realtime':
        return realtimeKeysCount || undefined;
      case 'video_api':
        return videoApiKeysCount || undefined;
      case 'meetings_api':
        return meetingsApiKeysCount || undefined;
      case 'embed':
        return embedKeysCount || undefined;
      default:
        return undefined;
    }
  };


  const MENU_OPTIONS = [
    {
      option: DASHBOARD,
      icon: <DashboardIcon />,
      link: "/dashboard",
      action: () => handleNavigation("/dashboard"),
      disabled: !isAdmin,
    },
    {
      option: USERS,
      icon: <Members />,
      link: "/meetings",
      action: () => handleNavigation("/meetings"),
      count: getMenuItemCount('meetings_seats'),
      disabled: !canAccessProduct("meetings"),

    },
    {
      option: API_KEYS,
      icon: <ApiKeys />,
      link: "/api-keys",
      action: () => handleNavigation("/api-keys"),
      count: getMenuItemCount('api'),
      disabled: !canAccessProduct("api"),
    },
    {
      option: REALTIME_API,
      icon: <ApiKeys />,
      link: "/realtime-api",
      action: () => handleNavigation("/realtime-api"),
      count: getMenuItemCount('realtime'),
      disabled: !canAccessProduct("realtime_api"),
    },
    {
      option: VIDEO_API,
      icon: <ApiKeys />,
      link: "/video-api",
      action: () => handleNavigation("/video-api"),
      count: getMenuItemCount('video_api'),
      disabled: !canAccessProduct("video_translation_api"),
    },
    {
      option: MEETINGS_API,
      icon: <ApiKeys />,
      link: "/meetings-api",
      action: () => handleNavigation("/meetings-api"),
      count: getMenuItemCount('meetings_api'),
      disabled: !canAccessProduct("meetings_api"),
    },
    {
      option: VIDEO_TRANSLATION,
      icon: <Video  size={20}/>,
      link: "/video-translation",
      action: () => handleNavigation("/video-translation"),
      //count: getMenuItemCount('video'),
      disabled: !canAccessProduct("video_tool"),
    },
    {
      option: PRESENTATIONS,
      icon: <Presentation size={20} />,
      link: "/presentations",
      action: () => handleNavigation("/presentations"),
      disabled: !canAccessProduct("presentations"),
    },
    {
      option: CONFERENCES,
      icon: <Users size={20} />,
      link: "/conferences",
      action: () => handleNavigation("/conferences"),
      disabled: !canAccessProduct("conferences"),
    },
    {
      option: WEBSITE_TRANSLATION,
      icon: <EmbedIcon />,
      link: "/emotii-embed",
      action: () => handleNavigation("/emotii-embed"),
      count: getMenuItemCount('embed'),
      disabled: !canAccessProduct("website_translation"),
    },
    {
      option: EMOTII_FOR_TEAMS,
      icon: <Users size={20} />,
      link: "/emotii-for-teams",
      action: () => handleNavigation("/emotii-for-teams"),
      count: getMenuItemCount('teams_seats'),
      disabled: !canAccessProduct("emotii_for_teams"),
    },
    {
      option: GLOSSARY,
      icon: <BookOpenText size={20} />,
      link: "/glossary",
      action: () => handleNavigation("/glossary"),
    },
    {
      option: SUBSCRIPTIONS,
      icon: <Subscription />,
      link: "/subscriptions",
      action: () => handleNavigation("/subscriptions"),
      // Disabled if user is not admin
      disabled: !isAdmin,
    },
  ];

  const BOTTOM_MENU_OPTIONS = [
    {
      option: SUPPORT,
      icon: <Support />,
      link: "/support",
      action: () => {
        setMobileMenuOpen(false);
        if (setShowSupportModal) {
          setShowSupportModal(true);
        }
      },
    },
    {
      option: PROJECT,
      icon: <Project />,
      link: "/project",
      action: () => handleNavigation("/project"),
    },
  ];

  return (
    <>
      {/* Mobile Header with Hamburger */}
      <MobileHeader>
        <HamburgerButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <HamburgerLine />
          <HamburgerLine />
          <HamburgerLine />
        </HamburgerButton>
        <MobileLogoWrapper>
          <LogoIcon />
        </MobileLogoWrapper>
      </MobileHeader>

      {/* Mobile Overlay */}
      {mobileMenuOpen && <MobileOverlay onClick={() => setMobileMenuOpen(false)} />}

      {/* Main Sidebar Container */}
      <Container ref={ref} mobileOpen={mobileMenuOpen}>
        <Content>
          <LogoSection>
            <img src="/emotii-1.svg" alt="Emotii" />
          </LogoSection>
          <ProjectsMenu setCreateNewProjectModalOpen={setCreateNewProjectModalOpen} />

          <MainMenuList>
            {MENU_OPTIONS.filter(item => !item.disabled).map((item, index) => (
              <MenuItem
                key={index}
                onClick={item.action}
                icon={item.icon}
                text={item.option}
                selected={window.location.pathname.includes(item.link)}
                count={item.count}
              />
            ))}
          </MainMenuList>

          <BottomSection>
            <BottomMenuList>
              {BOTTOM_MENU_OPTIONS.map((item, index) => (
                <MenuItem
                  key={index}
                  onClick={item.action}
                  icon={item.icon}
                  text={item.option}
                  selected={window.location.pathname.includes(item.link)}
                  disabled={item.disabled}
                />
              ))}
            </BottomMenuList>
            <UserProfileCard />
          </BottomSection>
        </Content>
      </Container>
    </>
  );
};

export default SideBarMenu;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background: ${lightThemeColors.background};
  border-right: 1px solid ${lightThemeColors.sidebar.menuItemBorder};
  align-items: center;
  height: 100%;
  min-width: 312px;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: ${props => props.mobileOpen ? '0' : '-250px'};
    width: 250px;
    height: 100vh;
    z-index: 1001;
    transition: left 0.3s ease-in-out;
    box-shadow: ${props => props.mobileOpen ? '2px 0 10px rgba(0,0,0,0.1)' : 'none'};
    background: ${lightThemeColors.background};
  }
`;

const ItemContainer = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;
  min-height: 40px;
  cursor: pointer;
  padding: 8px 12px;
  gap: 12px;
  transition: 200ms linear;
  position: relative;
  border-radius: 6px;
  margin-bottom: 2px;
  ${props => props.disabled ? `opacity:0.3; pointer-events: none;` : ``}

  ${props => props.selected ? `
    background: #FAFAFA;
    color: ${lightThemeColors.darkColor};

    svg {
      color: ${lightThemeColors.darkColor} !important;
    }

    p {
      color: ${lightThemeColors.darkColor} !important;
    }

    &:hover {
      background: #FAFAFA;
    }
  ` : `
    &:hover {
      background: ${lightThemeColors.menuBackground};
    }
  `}

  @media (max-width: 768px) {
    min-height: 45px;
    padding: 12px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  min-height: 20px;
  max-width: 20px;
  max-height: 20px;
  position: relative;
  transition: 200ms linear;
  flex-shrink: 0;

  svg {
    position: relative;
    height: 20px;
    width: 20px;
    object-fit: contain;
    color: ${(props) =>
    props.selected
      ? 'white'
      : lightThemeColors.darkColor50};
  }
`;

const ItemTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  p {
    font-weight: ${(props) => (props.selected ? "600" : "500")};
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
    color: ${(props) =>
    props.selected
      ? 'white'
      : lightThemeColors.darkColor};
  }

  @media (max-width: 768px) {
    p {
      font-size: 14px;
    }
  }
`;

const CountBadge = styled.span`
  background: ${props => props.theme === 'selected' ? lightThemeColors.darkColor : lightThemeColors.sidebar.projectsBg};
  color: ${props => props.theme === 'selected' ? 'white' : lightThemeColors.darkColor};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px 3px 8px;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const BottomSection = styled.div`
  margin-top: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    margin-top: auto;
    width: 100%;
  }
`;

const MainMenuList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  width: 100%;
  transition: 400ms linear;

  @media (max-width: 768px) {
    padding: 20px 0;
  }
`;

const BottomMenuList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const LogoSection = styled.div`
  padding: 24px 16px 16px 16px;
  display: flex;
  justify-content: flex-start;

  img {
    height: 32px;
    width: auto;
  }

  @media (max-width: 768px) {
    padding: 20px 16px 16px 16px;

    img {
      height: 28px;
    }
  }
`;


const SidebarLogoWrapper = styled.div`
  margin-left: ${(props) => (props.expanded ? "initial" : "auto")};
  margin-right: auto;
  padding: 30px 10px;
  svg {
    width: 100%;
    max-height: 24px;
  }
  
  @media (max-width: 768px) {
    padding: 20px 10px;
    margin: 0 auto;
    svg {
      max-height: 24px;
      width: auto;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  width: 100%;
  height: 100%;
  padding: 0 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 0 16px;
    height: 100%;
    overflow: auto;
  }
`;

// New mobile-specific components
const MobileHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 15px;
    background: ${lightThemeColors.background};
    border-bottom: 1px solid ${lightThemeColors.sidebar.menuItemBorder};
    padding: 15px 20px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    height: 60px;
    box-sizing: border-box;
  }
`;

const MobileLogoWrapper = styled.div`
  svg {
    height: 24px;
    width: auto;
  }
`;

const HamburgerButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const HamburgerLine = styled.div`
  width: 24px;
  height: 2px;
  background: ${lightThemeColors.darkColor};
  transition: all 0.3s linear;
`;

const MobileOverlay = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }
`;
